import type { AssetStyleId, WaveformShape } from 'fym-shared/src/music24/assetStyles';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import type { StudioThemeId } from 'fym-shared/src/music24/studioThemes';
import { SerializedKitAsset, SerializedKitAssetVersion } from '../../../../redux/api/fymAPI.types';
import { SerializedReel } from '../types';
import { AssetViewerItem, kitAssetViewerItem, reelViewerItem } from '../viewer/viewerItems';

/**
 * The revision history of one asset (Releases v2, design d1): same type + variant = the same
 * asset, every re-make is the next revision (r1, r2, r3). A kit asset reads its versions; a reel
 * reads the reels of the same song and `variant`. Pure data, so the rules are tested without React.
 */
export type RevisionState = 'ready' | 'failed' | 'working' | 'fileRemoved';

/** The files of the newest READY revisions stay; a failed revision does not count (owner answer 2). */
export const REVISIONS_WITH_FILES = 3;

export interface RevisionEntry {
  /** `kitAsset:<uuid>` or `reel:<uuid>`, the same key as the viewer. */
  key: string;
  kind: 'kitAsset' | 'reel';
  uuid: string;
  /** The revision number (the API `version` of a kit asset, `revision` of a reel). */
  number: number;
  state: RevisionState;
  /** 0..100 while it works. */
  progress: number;
  /** ISO: the time it was made (the render time of a ready one). */
  madeAt: string;
  maker: { id: number; email: string } | null;
  themeId: StudioThemeId;
  releaseThemeId: ReleaseThemeId;
  /** The style of a kit asset, the waveform of a reel. */
  style: AssetStyleId | null;
  waveform: WaveformShape | null;
  /** The text of the asset (a lyric line), or null. */
  text: string | null;
  palette: string[] | null;
  durationSec: number | null;
  /** The viewer item of a revision with a file; null when it has none. */
  viewerItem: AssetViewerItem | null;
  asset: SerializedKitAsset | null;
  reel: SerializedReel | null;
}

const WORKING_REEL = ['queued', 'analysing', 'planning', 'rendering'];

function kitState(asset: SerializedKitAsset): RevisionState {
  if (asset.status === 'failed') {
    return 'failed';
  }
  if (asset.status !== 'ready') {
    return 'working';
  }
  return asset.fileRemoved === true || asset.fileUrl === null ? 'fileRemoved' : 'ready';
}

/** The history of a kit asset from `GET .../assets/:uuid/versions`, newest first. */
export function kitRevisionEntries(versions: SerializedKitAssetVersion[]): RevisionEntry[] {
  return [...versions]
    .sort((a, b) => b.version - a.version)
    .map(
      (asset): RevisionEntry => ({
        key: `kitAsset:${asset.uuid}`,
        kind: 'kitAsset',
        uuid: asset.uuid,
        number: asset.version,
        state: kitState(asset),
        progress: asset.progress,
        madeAt: asset.renderedAt ?? asset.createdAt,
        maker: asset.createdBy ?? null,
        themeId: asset.themeId,
        releaseThemeId: asset.releaseThemeId,
        style: asset.style,
        waveform: asset.waveform,
        text: typeof asset.params.lyricLine === 'string' ? asset.params.lyricLine : null,
        palette: asset.palette,
        durationSec: asset.durationSec,
        viewerItem: kitAssetViewerItem(asset),
        asset,
        reel: null,
      })
    );
}

/** The variant of a reel; an older answer has none, so its length names it. */
export function reelVariantOf(reel: SerializedReel): string {
  return reel.variant ?? `${reel.durationSec}s`;
}

function reelState(reel: SerializedReel): RevisionState {
  if (reel.status === 'failed') {
    return 'failed';
  }
  return WORKING_REEL.includes(reel.status) ? 'working' : 'ready';
}

/**
 * The history of a reel: the reels of the same song and variant, newest first. A rejected reel
 * stays in the history (it was made). An old answer with no `revision` numbers by age.
 */
export function reelRevisionEntries(reels: SerializedReel[], reel: SerializedReel): RevisionEntry[] {
  const variant = reelVariantOf(reel);
  const same = reels
    .filter((one) => one.catalogItemUuid === reel.catalogItemUuid && reelVariantOf(one) === variant)
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  const list = same.some((one) => one.uuid === reel.uuid) ? same : [...same, reel];
  return list
    .map(
      (one, index): RevisionEntry => ({
        key: `reel:${one.uuid}`,
        kind: 'reel',
        uuid: one.uuid,
        number: one.revision ?? index + 1,
        state: reelState(one),
        progress: one.progress,
        madeAt: one.createdAt,
        maker: one.createdBy ?? null,
        themeId: 'aiReel',
        releaseThemeId: one.themeId,
        style: null,
        waveform: one.waveform,
        text: null,
        palette: null,
        durationSec: one.durationSec,
        viewerItem: reelViewerItem(one),
        asset: null,
        reel: one,
      })
    )
    .sort((a, b) => b.number - a.number);
}

/** The number of the next revision. */
export function nextRevisionNumber(entries: RevisionEntry[]): number {
  return Math.max(0, ...entries.map((entry) => entry.number)) + 1;
}

/**
 * The revision whose file goes away when a new revision becomes ready: the third newest ready
 * revision with a file now (the new one, and the two newer ones, keep theirs). Null when no file
 * goes. A failed revision has no file and does not count (owner answer 2).
 */
export function fileLostAfterNext(entries: RevisionEntry[]): RevisionEntry | null {
  const withFile = entries.filter((entry) => entry.state === 'ready').sort((a, b) => b.number - a.number);
  return withFile[REVISIONS_WITH_FILES - 1] ?? null;
}

/** The two newest revisions with a file: the first pair of Compare (design d2). */
export function defaultComparePair(entries: RevisionEntry[]): string[] {
  return entries
    .filter((entry) => entry.state === 'ready')
    .sort((a, b) => b.number - a.number)
    .slice(0, 2)
    .map((entry) => entry.key);
}

export type RevisionFactKey = 'theme' | 'style' | 'text' | 'colours' | 'length';

export interface RevisionChange {
  key: RevisionFactKey;
  from: string | null;
  to: string | null;
}

/** The raw value of one fact of a revision (labels come later, in the view). */
export function revisionFact(entry: RevisionEntry, key: RevisionFactKey): string | null {
  switch (key) {
    case 'theme':
      return entry.releaseThemeId;
    case 'style':
      return entry.style ?? entry.waveform;
    case 'text':
      return entry.text;
    case 'colours':
      return entry.palette === null ? null : entry.palette.join(',');
    case 'length':
      return entry.durationSec === null ? null : String(entry.durationSec);
  }
}

const FACT_KEYS: RevisionFactKey[] = ['theme', 'style', 'text', 'colours', 'length'];

/**
 * What changed from the revision before (design d1, "what changed"). The first revision has no
 * revision before it, so it has no change. A fact that one side does not know (null) is not a change.
 */
export function revisionChanges(entry: RevisionEntry, previous: RevisionEntry | undefined): RevisionChange[] {
  if (previous === undefined) {
    return [];
  }
  return FACT_KEYS.map((key) => ({ key, from: revisionFact(previous, key), to: revisionFact(entry, key) })).filter(
    (change) => change.from !== null && change.to !== null && change.from !== change.to
  );
}

/** The rows of the Compare table (design d2): each fact of the two revisions, and whether it differs. */
export function compareRows(
  a: RevisionEntry,
  b: RevisionEntry
): Array<{ key: RevisionFactKey; a: string | null; b: string | null; differs: boolean }> {
  return FACT_KEYS.map((key) => {
    const left = revisionFact(a, key);
    const right = revisionFact(b, key);
    return { key, a: left, b: right, differs: left !== right };
  }).filter((row) => row.a !== null || row.b !== null);
}
